<script lang="ts" setup>
import Product from "@/models/Product";
import WishlistItem, {
  type ICustomerWishlistItem,
} from "@/models/CustomerWishlistItem";
import { computed, ref, watch, type PropType } from "vue";
import auth from "@/services/AuthService";
import CustomerWishlist, {
  type ICustomerWishlist,
} from "@/models/CustomerWishlist";
import { notify } from "@kyvg/vue3-notification";
import CustomerWishlistItem from "@/models/CustomerWishlistItem";
import useSearchStore from "~/stores/search";

enum WishlistItemResultType {
  None = 1,
  AddError = 2,
  AddSuccess = 3,
  RemoveError = 4,
  RemoveSuccess = 5,
}

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
  initWishlists: {
    type: Array as PropType<ICustomerWishlist[]>,
    default: () => [],
  },
  initWishlistItems: {
    type: Array as PropType<ICustomerWishlistItem[]>,
    default: () => [],
  },
});

const searchStore = useSearchStore();
const {
  wishlistItems: storeWishlistItems,
  wishlists: storeWishlists,
  loadingWishlist,
  wishlistLoaded,
} = storeToRefs(searchStore);

const wishlistItem = ref<WishlistItem>();
const addingOrRemoving = ref(false);
const lastResultType = ref<WishlistItemResultType>();
const wishlists = ref<CustomerWishlist[]>([]);
const firstCustomerWishlist = computed(
  (): CustomerWishlist | undefined => wishlists.value.at(0) as CustomerWishlist,
);
const user = auth.getUser();
const loading = ref(false);
const loadingError = ref(false);
const providedWishlists = computed(
  () => props.initWishlists as ICustomerWishlist[],
);
const providedWishlistItems = computed(
  () => props.initWishlistItems as ICustomerWishlistItem[],
);

const getCustomerWishlists = async (
  initWishlists: ICustomerWishlist[],
  allowNetwork: boolean,
) => {
  if (initWishlists.length > 0) {
    wishlists.value = CustomerWishlist.fromArray(
      JSON.parse(JSON.stringify(initWishlists)) as ICustomerWishlist[],
    );
  } else if (allowNetwork && user && user.id) {
    wishlists.value = await CustomerWishlist.loadWishlistsOnce(user.id, true);
    if (wishlists.value.length > 0) {
      searchStore.$patch({ wishlists: wishlists.value });
    }
  }
};

const getProductWishlistItems = async (
  initItems?: ICustomerWishlistItem[] | CustomerWishlistItem[],
  allowNetwork: boolean = false,
) => {
  if (props.product.id) {
    let items: CustomerWishlistItem[] = [];
    if (initItems && initItems.length > 0) {
      items = CustomerWishlistItem.fromArray(
        JSON.parse(
          JSON.stringify(
            initItems.filter((item) => item.product?.id == props.product.id),
          ),
        ) as ICustomerWishlistItem[],
      );
    } else if (allowNetwork && user && user.id) {
      items = await CustomerWishlistItem.getItemsByUserAndProduct(user.id, [
        props.product.id,
      ]);
    }
    wishlistItem.value = items.at(0);
  }
};

const loadWishlistData = async (allowNetwork: boolean = false) => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  loadingError.value = false;
  try {
    const sourceWishlists = providedWishlists.value.length
      ? providedWishlists.value
      : storeWishlists.value;
    const sourceWishlistItems = providedWishlistItems.value.length
      ? providedWishlistItems.value
      : (storeWishlistItems.value as WishlistItem[]);

    await getCustomerWishlists(sourceWishlists, allowNetwork);
    await getProductWishlistItems(sourceWishlistItems, allowNetwork);
  } catch (err) {
    console.log(err);
    loadingError.value = true;
  }
  loading.value = false;
};

const route = useRoute();
const isSearchRoute = computed(
  () => route.name === "search" || route.path.startsWith("/buscar"),
);
const hasStoreData = computed(
  () => storeWishlists.value.length > 0 || storeWishlistItems.value.length > 0,
);
const hasProvidedData = computed(
  () =>
    providedWishlists.value.length > 0 ||
    providedWishlistItems.value.length > 0,
);

const syncStoreWishlistItem = (item?: WishlistItem) => {
  if (!item?.product?.id) {
    return;
  }

  const nextItems = [...storeWishlistItems.value];
  const existingIndex = nextItems.findIndex(
    (storeItem) => storeItem.product?.id === item.product?.id,
  );

  if (existingIndex >= 0) {
    nextItems[existingIndex] = item;
  } else {
    nextItems.push(item);
  }

  searchStore.$patch({ wishlistItems: nextItems });
};

const removeStoreWishlistItem = (productId?: string | null) => {
  if (!productId) {
    return;
  }

  searchStore.$patch({
    wishlistItems: storeWishlistItems.value.filter(
      (item) => item.product?.id !== productId,
    ),
  });
};

watch(
  [
    loadingWishlist,
    wishlistLoaded,
    hasStoreData,
    hasProvidedData,
    isSearchRoute,
  ],
  () => {
    if (!loadingWishlist.value) {
      if (hasProvidedData.value || wishlistLoaded.value || hasStoreData.value) {
        loadWishlistData(false);
        return;
      }
      if (!isSearchRoute.value) {
        loadWishlistData(true);
      }
    }
  },
  {
    immediate: true,
  },
);

const notifyResult = () => {
  let title = "";
  let text = "";
  let type = "warn";
  switch (lastResultType.value) {
    case WishlistItemResultType.AddError:
      title = "Ocurrió un problema";
      text =
        "No se pudo agregar el producto de tus favoritos, recarga la página e intenta de nuevo.";
      break;
    case WishlistItemResultType.RemoveError:
      title = "Ocurrió un problema";
      text =
        "No se pudo quitar el producto de tus favoritos, recarga la página e intenta de nuevo.";
      break;
    case WishlistItemResultType.AddSuccess:
      title = "Producto agregado";
      text = "¡Producto agregado con éxito a tus favoritos!";
      type = "success";
      break;
    case WishlistItemResultType.RemoveSuccess:
      title = "Producto eliminado";
      text = "Producto eliminado de tus favoritos";
      type = "warn";
      break;
  }
  notify({
    type: type,
    title: title,
    text: text,
    duration: 3000,
    ignoreDuplicates: false,
  });
};

const addItem = async () => {
  if (
    addingOrRemoving.value ||
    wishlistItem.value ||
    !firstCustomerWishlist.value
  ) {
    return;
  }
  addingOrRemoving.value = true;
  lastResultType.value = WishlistItemResultType.None;
  try {
    const newItem = new WishlistItem();
    newItem.product = props.product;
    newItem.wishlist = firstCustomerWishlist.value;
    await newItem.save();
    wishlistItem.value = newItem;
    syncStoreWishlistItem(newItem);
    lastResultType.value = WishlistItemResultType.AddSuccess;
  } catch (err) {
    console.error(err);
    lastResultType.value = WishlistItemResultType.AddError;
  } finally {
    addingOrRemoving.value = false;
    notifyResult();
  }
};

const removeItem = async () => {
  if (
    addingOrRemoving.value ||
    !wishlistItem.value ||
    !firstCustomerWishlist.value
  ) {
    return;
  }
  addingOrRemoving.value = true;
  lastResultType.value = WishlistItemResultType.None;
  try {
    const productId = wishlistItem.value.product?.id;
    await wishlistItem.value.delete();
    wishlistItem.value = undefined;
    removeStoreWishlistItem(productId);
    lastResultType.value = WishlistItemResultType.RemoveSuccess;
  } catch (err) {
    console.error(err);
    lastResultType.value = WishlistItemResultType.RemoveError;
  } finally {
    addingOrRemoving.value = false;
    notifyResult();
  }
};
</script>

<template>
  <div v-if="user && user.id">
    <template v-if="firstCustomerWishlist && !loading">
      <button
        v-if="wishlistItem"
        type="button"
        class="btn btn-light"
        :disabled="addingOrRemoving"
        aria-label="Quitar de favoritos"
        @click.prevent="removeItem()"
      >
        <span
          v-if="addingOrRemoving"
          class="spinner-border spinner-border-sm"
        ></span>
        <i v-else class="bi bi-heart-fill text-danger"></i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-secondary opacity-75"
        aria-label="Agregar a favoritos"
        :disabled="addingOrRemoving"
        @click.prevent="addItem()"
      >
        <span
          v-if="addingOrRemoving"
          class="spinner-border spinner-border-sm"
        ></span>
        <i v-else class="bi bi-heart"></i>
      </button>
    </template>
    <template v-else-if="loading">
      <button
        type="button"
        class="btn btn-outline-secondary opacity-75"
        aria-label="Cargando favorito"
        :disabled="true"
        @click.prevent
      >
        <span class="spinner-border spinner-border-sm"></span>
      </button>
    </template>
    <template v-else-if="loadingError && user">
      <span class="small text-danger">Error con botón de favorito</span>
    </template>
  </div>
</template>
